// =============================================================================
// SpawnProductDialog — "Create Product from this Design" modal
// -----------------------------------------------------------------------------
// Loaded on the My Design detail page when the merchant clicks the primary
// action. Posts to `POST /api/v1/designs/:libraryDesignId/spawn-product`
// after the merchant picks variants + supplies a title.
//
// We do NOT re-open the designer: the library design payload is the
// authoritative source server-side. The merchant only picks which BlankVariant
// rows the new product should expose; everything else (designJson, uptId) is
// resolved by the API from the library row.
//
// Idempotency: a fresh UUIDv4 is generated when the dialog mounts and sent as
// `Idempotency-Key`. The same key is reused across retries so a transient
// network blip never produces a duplicate product.
// =============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Badge, Button, Input, Spinner } from '@tkpod/ui';
import { useApiClient } from '@tkpod/api-client';
import { PRODUCT_LIMITS } from '@tkpod/shared';
import { generateUuidV4 } from '../lib/uuid';

const MAX_VARIANTS = PRODUCT_LIMITS.MAX_VARIANTS_PER_PRODUCT;

interface UptVariant {
  id: string;
  sku: string;
  cost: string;
  isActive: boolean;
  options: Record<string, string>;
}

interface UptBlankProduct {
  id: string;
  name: string;
  brand: string;
  model: string;
  isPrimary: boolean;
  variants: UptVariant[];
}

interface UptDetail {
  id: string;
  name: string;
  type: string;
  blankProducts: UptBlankProduct[];
}

interface SpawnResponse {
  data: {
    designId: string;
    designRefId: string;
    libraryDesignId: string;
    libraryDesignRefId: string;
    productId: string;
    variantIds: string[];
  };
}

export interface SpawnProductDialogProps {
  libraryDesignId: string;
  uptId: string;
  defaultTitle: string;
  onClose: () => void;
  onSuccess: (productId: string) => void;
}

export function SpawnProductDialog({
  libraryDesignId,
  uptId,
  defaultTitle,
  onClose,
  onSuccess,
}: SpawnProductDialogProps) {
  const api = useApiClient();
  const [title, setTitle] = useState(defaultTitle);
  const [description, setDescription] = useState('');
  const [selectedVariantIds, setSelectedVariantIds] = useState<Set<string>>(
    () => new Set(),
  );

  // Generate the idempotency key ONCE per dialog mount so retries from
  // the same dialog instance reuse the same key (server returns the
  // cached response).
  const idempotencyKeyRef = useRef<string>(generateUuidV4());

  const templateQuery = useQuery({
    queryKey: ['portal', 'catalog-template', uptId],
    queryFn: () =>
      api.get<{ data: UptDetail }>(`/api/v1/catalog/templates/${uptId}`),
    enabled: !!uptId,
  });

  const template = templateQuery.data?.data ?? null;

  // First load: auto-select every active variant. Most apparel spawn
  // flows want "all variants" by default; the merchant can deselect
  // unwanted ones before confirming. Effect keyed on `template?.id`
  // so re-rendering with the same template doesn't clobber the
  // merchant's manual deselections.
  const templateId = template?.id ?? null;
  useEffect(() => {
    if (!template) return;
    const ids = new Set<string>();
    for (const bp of template.blankProducts) {
      for (const v of bp.variants) {
        if (v.isActive !== false) ids.add(v.id);
      }
    }
    setSelectedVariantIds(ids);
  }, [templateId, template]);

  const totalVariants = useMemo(() => {
    if (!template) return 0;
    return template.blankProducts.reduce(
      (acc, bp) => acc + bp.variants.filter((v) => v.isActive !== false).length,
      0,
    );
  }, [template]);

  const spawnMutation = useMutation({
    mutationFn: async () => {
      return api.post<SpawnResponse>(
        `/api/v1/designs/${libraryDesignId}/spawn-product`,
        {
          productTitle: title.trim(),
          productDescription: description.trim() || undefined,
          selectedBlankVariantIds: [...selectedVariantIds],
        },
        {
          headers: { 'Idempotency-Key': idempotencyKeyRef.current },
        },
      );
    },
    onSuccess: (res) => {
      const productId = res.data.productId;
      if (productId) onSuccess(productId);
    },
  });

  const toggleVariant = (id: string) => {
    setSelectedVariantIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleBlankProduct = (bp: UptBlankProduct) => {
    setSelectedVariantIds((prev) => {
      const next = new Set(prev);
      const active = bp.variants.filter((v) => v.isActive !== false);
      const allSelected = active.every((v) => next.has(v.id));
      if (allSelected) {
        for (const v of active) next.delete(v.id);
      } else {
        for (const v of active) next.add(v.id);
      }
      return next;
    });
  };

  const tooManyVariants = selectedVariantIds.size > MAX_VARIANTS;
  const canSubmit =
    title.trim().length > 0 &&
    selectedVariantIds.size > 0 &&
    !tooManyVariants &&
    !spawnMutation.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="spawn-dialog-title"
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-border bg-surface shadow-xl">
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2
            id="spawn-dialog-title"
            className="text-base font-semibold text-text-primary"
          >
            Create product from this design
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-text-secondary transition-colors hover:bg-surface-secondary"
            aria-label="Close"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-text-tertiary">
              Product title
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Storefront title shown to customers"
              maxLength={200}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-text-tertiary">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Product description (Markdown ok, ≤ 5000 chars)"
              maxLength={5000}
              rows={3}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="block text-xs font-semibold uppercase tracking-wider text-text-tertiary">
                Variants
              </label>
              <Badge variant="default" size="sm">
                {selectedVariantIds.size} / {totalVariants} selected
              </Badge>
            </div>

            {tooManyVariants ? (
              <p className="mb-2 text-xs text-danger">
                A product supports at most {MAX_VARIANTS} variants (Shopify
                limit). {selectedVariantIds.size} selected. Deselect some
                variants to continue.
              </p>
            ) : null}

            {templateQuery.isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="md" />
              </div>
            ) : templateQuery.isError || !template ? (
              <p className="text-xs text-danger">
                Failed to load template variants — please retry.
              </p>
            ) : (
              <div className="space-y-3">
                {template.blankProducts.map((bp) => {
                  const active = bp.variants.filter(
                    (v) => v.isActive !== false,
                  );
                  if (active.length === 0) return null;
                  const allSelected = active.every((v) =>
                    selectedVariantIds.has(v.id),
                  );
                  return (
                    <div
                      key={bp.id}
                      className="rounded-md border border-border bg-surface"
                    >
                      <div className="flex items-center justify-between border-b border-border px-3 py-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-text-primary">
                            {bp.name}
                          </p>
                          <p className="text-[11px] text-text-tertiary">
                            {bp.brand} · {bp.model} · {active.length} variant
                            {active.length === 1 ? '' : 's'}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleBlankProduct(bp)}
                        >
                          {allSelected ? 'Deselect all' : 'Select all'}
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-1 p-2 sm:grid-cols-3">
                        {active.map((v) => {
                          const checked = selectedVariantIds.has(v.id);
                          return (
                            <label
                              key={v.id}
                              className={`flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1.5 text-xs transition-colors ${
                                checked
                                  ? 'border-brand-300 bg-brand-50 text-text-primary'
                                  : 'border-border bg-surface text-text-secondary hover:bg-surface-secondary'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleVariant(v.id)}
                                className="h-3.5 w-3.5"
                              />
                              <span className="truncate">
                                {formatVariantLabel(v)}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {spawnMutation.isError && (
            <p className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-xs text-danger">
              {(spawnMutation.error as Error)?.message ?? 'Failed to create product'}
            </p>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!canSubmit}
            onClick={() => spawnMutation.mutate()}
          >
            {spawnMutation.isPending ? 'Creating…' : 'Create product'}
          </Button>
        </footer>
      </div>
    </div>
  );
}

function formatVariantLabel(v: UptVariant): string {
  const options = v.options ?? {};
  const parts = Object.entries(options)
    .filter(([, val]) => typeof val === 'string' && val.length > 0)
    .map(([, val]) => val);
  if (parts.length > 0) return parts.join(' / ');
  return v.sku;
}

